import { computed, ref, watch } from 'vue';
import { createRouter, createWebHistory, type RouteLocationRaw, type RouterLinkProps } from 'vue-router';
import { loginState, userLoginStateConfirmedPromise } from '../cross-app-frontend/firebase';
import { facilitiesByFacilityID, facilitiesList, facilityFindFromRoute } from '../cross-app-frontend/facilities';
import type {
  EmissionsLimitID,
  EmissionsUnitID,
  FacilityID,
  HazWasteStreamID,
  ProductID,
  ReleaseProfileID,
  TenantID,
  UserID,
} from '../tri-shared/types/db';
import { slugFromName } from '../tri-shared/services/formatting';
import type { RouteRecordInfo } from 'vue-router';
import type { FacilityBrief } from '../cross-app-shared/types/common';
import { appTitle, face, type Face } from '@/services/navigation/navigation';

export interface RouteNamedMap {
  'HazWaste Help': RouteRecordInfo<
    'HazWaste Help',
    '/facility/:facilityID-:facilitySlug/hazwaste-help',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string }
  >;
  'HazWaste Help Article': RouteRecordInfo<
    'HazWaste Help Article',
    '/facility/:facilityID-:facilitySlug/hazwaste-help/:articleID',
    { facilityID: string | FacilityID; facilitySlug: string; articleID: string },
    { facilityID: string; facilitySlug: string; articleID: string }
  >;
  Welcome: RouteRecordInfo<'Welcome', '/', Record<never, never>, Record<never, never>, never>;
  'Welcome Redirect': RouteRecordInfo<
    'Welcome Redirect',
    '/welcome',
    Record<never, never>,
    Record<never, never>,
    never
  >;
  Login: RouteRecordInfo<'Login', '/login', Record<never, never>, Record<never, never>, never>;
  'HazWaste Container Field': RouteRecordInfo<
    'HazWaste Container Field',
    '/field/hazwaste-container/:fieldLabelToken',
    { fieldLabelToken: string },
    { fieldLabelToken: string },
    never
  >;
  'HazWaste Accumulation Areas': RouteRecordInfo<
    'HazWaste Accumulation Areas',
    '/facility/:facilityID-:facilitySlug/hazwaste-accumulation-areas',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Site Map': RouteRecordInfo<
    'HazWaste Site Map',
    '/facility/:facilityID-:facilitySlug/hazwaste-site-map',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Quick Reference Guide': RouteRecordInfo<
    'HazWaste Quick Reference Guide',
    '/facility/:facilityID-:facilitySlug/hazwaste-quick-reference-guide',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Default Redirect': RouteRecordInfo<
    'Facility Default Redirect',
    '/:year(\\d{4})/:destination',
    { year: string | number; destination: string },
    { year: string; destination: string },
    never
  >;
  'Facility Status': RouteRecordInfo<
    'Facility Status',
    '/:year/facility/:facilityID-:facilitySlug',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Overall Status': RouteRecordInfo<
    'Overall Status',
    '/:year/facility/:facilityID-:facilitySlug/overall',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  Anomalies: RouteRecordInfo<
    'Anomalies',
    '/:year/facility/:facilityID-:facilitySlug/anomalies',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Mass Actions': RouteRecordInfo<
    'Mass Actions',
    '/:year/facility/:facilityID-:facilitySlug/mass-actions',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Usage': RouteRecordInfo<
    'Facility Usage',
    '/:year/facility/:facilityID-:facilitySlug/usage',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Activity Review': RouteRecordInfo<
    'Facility Activity Review',
    '/:year/facility/:facilityID-:facilitySlug/activity-review',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Products': RouteRecordInfo<
    'Facility Products',
    '/:year/facility/:facilityID-:facilitySlug/products',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    'Product'
  >;
  Product: RouteRecordInfo<
    'Product',
    '/:year/facility/:facilityID-:facilitySlug/products/:productID-:productSlug',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      productID: string | ProductID;
      productSlug: string;
    },
    { year: string; facilityID: string; facilitySlug: string; productID: string; productSlug: string },
    never
  >;
  'Facility Thresholds': RouteRecordInfo<
    'Facility Thresholds',
    '/:year/facility/:facilityID-:facilitySlug/chemicals',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Release Profiles': RouteRecordInfo<
    'Facility Release Profiles',
    '/:year/facility/:facilityID-:facilitySlug/release-profiles',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Release Profiles': RouteRecordInfo<
    'Release Profiles',
    '/:year/facility/:facilityID-:facilitySlug/release-profiles/list',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Release Profile': RouteRecordInfo<
    'Release Profile',
    '/:year/facility/:facilityID-:facilitySlug/release-profiles/:releaseProfileID-:releaseProfileSlug',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      releaseProfileID: string | ReleaseProfileID;
      releaseProfileSlug: string;
    },
    { year: string; facilityID: string; facilitySlug: string; releaseProfileID: string; releaseProfileSlug: string },
    never
  >;
  'Compliance Metrics': RouteRecordInfo<
    'Compliance Metrics',
    '/:year/facility/:facilityID-:facilitySlug/compliance-metrics/list',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Compliance Metric': RouteRecordInfo<
    'Compliance Metric',
    '/:year/facility/:facilityID-:facilitySlug/compliance-metrics/:releaseProfileID-:releaseProfileSlug',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      releaseProfileID: string | ReleaseProfileID;
      releaseProfileSlug: string;
    },
    { year: string; facilityID: string; facilitySlug: string; releaseProfileID: string; releaseProfileSlug: string },
    never
  >;
  'Inventory Profiles': RouteRecordInfo<
    'Inventory Profiles',
    '/:year/facility/:facilityID-:facilitySlug/inventory-profiles/list',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Inventory Profile': RouteRecordInfo<
    'Inventory Profile',
    '/:year/facility/:facilityID-:facilitySlug/inventory-profiles/:releaseProfileID-:releaseProfileSlug',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      releaseProfileID: string | ReleaseProfileID;
      releaseProfileSlug: string;
    },
    { year: string; facilityID: string; facilitySlug: string; releaseProfileID: string; releaseProfileSlug: string },
    never
  >;
  'Facility Questionnaire': RouteRecordInfo<
    'Facility Questionnaire',
    '/:year/facility/:facilityID-:facilitySlug/questionnaire',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Release Review': RouteRecordInfo<
    'Facility Release Review',
    '/:year/facility/:facilityID-:facilitySlug/releases',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Form R Details': RouteRecordInfo<
    'Facility Form R Details',
    '/:year/facility/:facilityID-:facilitySlug/form-r-details',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Form R': RouteRecordInfo<
    'Facility Form R',
    '/:year/facility/:facilityID-:facilitySlug/form-r',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Form R Submit': RouteRecordInfo<
    'Facility Form R Submit',
    '/:year/facility/:facilityID-:facilitySlug/submit',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Facility Exemption Review': RouteRecordInfo<
    'Facility Exemption Review',
    '/:year/facility/:facilityID-:facilitySlug/exemption-review',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  Receptors: RouteRecordInfo<
    'Receptors',
    '/:year/facility/:facilityID-:facilitySlug/receptors',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  Chemicals: RouteRecordInfo<
    'Chemicals',
    '/:year/facility/:facilityID-:facilitySlug/chemicals',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Chemicals Sync': RouteRecordInfo<
    'Chemicals Sync',
    '/:year/facility/:facilityID-:facilitySlug/chemicals/sync',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  Facilities: RouteRecordInfo<
    'Facilities',
    '/:year/facility/:facilityID-:facilitySlug/facilities',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'E-Manifest': RouteRecordInfo<
    'E-Manifest',
    '/:year/facility/:facilityID-:facilitySlug/e-manifest',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Admin Release Comparison': RouteRecordInfo<
    'Admin Release Comparison',
    '/:year/facility/:facilityID-:facilitySlug/release-comparison',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Admin Release Profile Assignments Comparison': RouteRecordInfo<
    'Admin Release Profile Assignments Comparison',
    '/:year/facility/:facilityID-:facilitySlug/release-profile-assignments-comparison',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Admin Release Snapshot Comparison': RouteRecordInfo<
    'Admin Release Snapshot Comparison',
    '/:year/facility/:facilityID-:facilitySlug/release-snapshot-comparison',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Usage Import': RouteRecordInfo<
    'Usage Import',
    '/:year/facility/:facilityID-:facilitySlug/usage-import',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'Spreadsheet Agent': RouteRecordInfo<
    'Spreadsheet Agent',
    '/:year/facility/:facilityID-:facilitySlug/spreadsheet-agent',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  Users: RouteRecordInfo<
    'Users',
    '/:year/facility/:facilityID-:facilitySlug/users',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'TRI Knowledge': RouteRecordInfo<
    'TRI Knowledge',
    '/:year/facility/:facilityID-:facilitySlug/help/tri-knowledge',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      section?: 'instructions' | 'qa' | 'articles' | '';
    },
    { year: string; facilityID: string; facilitySlug: string; section?: string },
    never
  >;
  'App Help': RouteRecordInfo<
    'App Help',
    '/:year/facility/:facilityID-:facilitySlug/help/app-help',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;

  // Emissions:
  'Emissions Unit Types': RouteRecordInfo<
    'Emissions Unit Types',
    '/:year/facility/:facilityID-:facilitySlug/emissions-unit-types',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;
  'Emissions Units': RouteRecordInfo<
    'Emissions Units',
    '/:year/facility/:facilityID-:facilitySlug/emissions-units',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;
  'Emissions Questionnaire': RouteRecordInfo<
    'Emissions Questionnaire',
    '/:year/facility/:facilityID-:facilitySlug/emissions-units/:emissionsUnitID-:emissionsUnitSlug/questionnaire',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      emissionsUnitID: string | EmissionsUnitID;
      emissionsUnitSlug: string;
    },
    {
      year: string;
      facilityID: string;
      facilitySlug: string;
      emissionsUnitID: string;
      emissionsUnitSlug: string;
    }
  >;
  Emissions: RouteRecordInfo<
    'Emissions',
    '/:year/facility/:facilityID-:facilitySlug/emissions-units/:emissionsUnitID-:emissionsUnitSlug/emissions',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      emissionsUnitID: string | EmissionsUnitID;
      emissionsUnitSlug: string;
    },
    {
      year: string;
      facilityID: string;
      facilitySlug: string;
      emissionsUnitID: string;
      emissionsUnitSlug: string;
    }
  >;
  'Emissions Reports': RouteRecordInfo<
    'Emissions Reports',
    '/:year/facility/:facilityID-:facilitySlug/emissions-reports',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;
  'Emissions Reports (Unit-Scoped)': RouteRecordInfo<
    'Emissions Reports (Unit-Scoped)',
    '/:year/facility/:facilityID-:facilitySlug/emissions-units/:emissionsUnitID-:emissionsUnitSlug/reports',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      emissionsUnitID: string | EmissionsUnitID;
      emissionsUnitSlug: string;
    },
    {
      year: string;
      facilityID: string;
      facilitySlug: string;
      emissionsUnitID: string;
      emissionsUnitSlug: string;
    },
    never
  >;
  'Emissions Overview': RouteRecordInfo<
    'Emissions Overview',
    '/:year/facility/:facilityID-:facilitySlug/emissions-overview',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;
  'Emissions Limits': RouteRecordInfo<
    'Emissions Limits',
    '/:year/facility/:facilityID-:facilitySlug/emissions-compliance',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;
  'Emissions Limit History': RouteRecordInfo<
    'Emissions Limit History',
    '/:year/facility/:facilityID-:facilitySlug/emissions-compliance/:emissionsLimitID/history',
    {
      year: string | number;
      facilityID: string | FacilityID;
      facilitySlug: string;
      emissionsLimitID: string | EmissionsLimitID;
    },
    { year: string; facilityID: string; facilitySlug: string; emissionsLimitID: string },
    never
  >;
  'Emissions My Products': RouteRecordInfo<
    'Emissions My Products',
    '/:year/facility/:facilityID-:facilitySlug/emissions-my-products',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string }
  >;

  // Utility:
  'No Access': RouteRecordInfo<'No Access', '/no-access', Record<never, never>, Record<never, never>, never>;
  Raffle: RouteRecordInfo<'Raffle', '/raffle', Record<never, never>, Record<never, never>, never>;
  Platform: RouteRecordInfo<
    'Platform',
    '/platform/:tenantID?/:facilityID?',
    { tenantID?: string | TenantID; facilityID?: string | FacilityID },
    { tenantID?: string; facilityID?: string }
  >;
  'Not Found': RouteRecordInfo<'Not Found', '/:pathMatch(.*)*', Record<never, never>, Record<never, never>, never>;

  // TierII:
  'TierII Overview': RouteRecordInfo<
    'TierII Overview',
    '/facility/:facilityID-:facilitySlug/overview',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'TierII Inventory': RouteRecordInfo<
    'TierII Inventory',
    '/:year/facility/:facilityID-:facilitySlug/inventory',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'TierII Inventory Questionnaire': RouteRecordInfo<
    'TierII Inventory Questionnaire',
    '/:year/facility/:facilityID-:facilitySlug/inventory/questionnaire/:productID?',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string; productID?: string | ProductID },
    { year: string; facilityID: string; facilitySlug: string; productID?: string },
    never
  >;
  'TierII Inventory Threshold': RouteRecordInfo<
    'TierII Inventory Threshold',
    '/:year/facility/:facilityID-:facilitySlug/inventory/threshold',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'TierII Report Form': RouteRecordInfo<
    'TierII Report Form',
    '/:year/facility/:facilityID-:facilitySlug/report-form/:sectionKey?',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string; sectionKey?: string },
    { year: string; facilityID: string; facilitySlug: string; sectionKey?: string },
    never
  >;
  'TierII Site Map': RouteRecordInfo<
    'TierII Site Map',
    '/:year/facility/:facilityID-:facilitySlug/site-map',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'TierII Export': RouteRecordInfo<
    'TierII Export',
    '/:year/facility/:facilityID-:facilitySlug/export',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'TierII Status': RouteRecordInfo<
    'TierII Status',
    '/:year/facility/:facilityID-:facilitySlug/status',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;

  // HazWaste:
  'HazWaste Overview': RouteRecordInfo<
    'HazWaste Overview',
    '/facility/:facilityID-:facilitySlug/hazwaste-overview',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Training Profile': RouteRecordInfo<
    'HazWaste Training Profile',
    '/facility/:facilityID-:facilitySlug/hazwaste-training-profile',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Training Management': RouteRecordInfo<
    'HazWaste Training Management',
    '/facility/:facilityID-:facilitySlug/hazwaste-training-management',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Training Configuration': RouteRecordInfo<
    'HazWaste Training Configuration',
    '/facility/:facilityID-:facilitySlug/hazwaste-training-configuration',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Training Personnel Detail': RouteRecordInfo<
    'HazWaste Training Personnel Detail',
    '/facility/:facilityID-:facilitySlug/hazwaste-training-management/personnel/:userID',
    { facilityID: string | FacilityID; facilitySlug: string; userID: string | UserID },
    { facilityID: string; facilitySlug: string; userID: string },
    never
  >;
  'HazWaste Waste Streams': RouteRecordInfo<
    'HazWaste Waste Streams',
    '/facility/:facilityID-:facilitySlug/hazwaste-streams',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Waste Stream Detail': RouteRecordInfo<
    'HazWaste Waste Stream Detail',
    '/facility/:facilityID-:facilitySlug/hazwaste-streams/:streamID-:streamSlug',
    { facilityID: string | FacilityID; facilitySlug: string; streamID: string | HazWasteStreamID; streamSlug: string },
    { facilityID: string; facilitySlug: string; streamID: string; streamSlug: string },
    never
  >;
  'HazWaste Waste Stream New': RouteRecordInfo<
    'HazWaste Waste Stream New',
    '/facility/:facilityID-:facilitySlug/hazwaste-streams/new',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Accumulation Area New': RouteRecordInfo<
    'HazWaste Accumulation Area New',
    '/facility/:facilityID-:facilitySlug/hazwaste-accumulation-areas/new',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Accumulation Area Detail': RouteRecordInfo<
    'HazWaste Accumulation Area Detail',
    '/facility/:facilityID-:facilitySlug/hazwaste-accumulation-areas/:locationID-:locationSlug',
    { facilityID: string | FacilityID; facilitySlug: string; locationID: string | number; locationSlug: string },
    { facilityID: string; facilitySlug: string; locationID: string; locationSlug: string },
    never
  >;
  'HazWaste Container Labels': RouteRecordInfo<
    'HazWaste Container Labels',
    '/facility/:facilityID-:facilitySlug/hazwaste-container-labels',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Inspections': RouteRecordInfo<
    'HazWaste Inspections',
    '/facility/:facilityID-:facilitySlug/hazwaste-inspections',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Tasks': RouteRecordInfo<
    'HazWaste Tasks',
    '/facility/:facilityID-:facilitySlug/hazwaste-tasks',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Manifests': RouteRecordInfo<
    'HazWaste Manifests',
    '/facility/:facilityID-:facilitySlug/hazwaste-manifests',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Other Shipments': RouteRecordInfo<
    'HazWaste Other Shipments',
    '/facility/:facilityID-:facilitySlug/hazwaste-other-shipments',
    { facilityID: string | FacilityID; facilitySlug: string },
    { facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Generation Log': RouteRecordInfo<
    'HazWaste Generation Log',
    '/:year/facility/:facilityID-:facilitySlug/hazwaste-generation-log',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
  'HazWaste Biennial Report': RouteRecordInfo<
    'HazWaste Biennial Report',
    '/:year/facility/:facilityID-:facilitySlug/hazwaste-biennial-report',
    { year: string | number; facilityID: string | FacilityID; facilitySlug: string },
    { year: string; facilityID: string; facilitySlug: string },
    never
  >;
}

declare module 'vue-router' {
  interface TypesConfig {
    RouteNamedMap: RouteNamedMap;
  }

  interface RouteMeta {
    face?: Face;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Welcome',
      component: () => import('@/pages/WelcomePage.vue'),
    },
    {
      path: '/welcome',
      name: 'Welcome Redirect',
      redirect: { name: 'Welcome' },
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../cross-app-frontend/LoginPage.vue'),
    },

    {
      path: '/field/hazwaste-container/:fieldLabelToken',
      name: 'HazWaste Container Field',
      props: true,
      meta: { face: 'hazwaste' },
      component: () => import('@/hazwaste/pages/FieldContainerPage.vue'),
    },
    {
      path: '/facility/:facilityID-:facilitySlug/overview',
      name: 'TierII Overview',
      props: true,
      meta: { face: 'tierii' },
      component: () => import('@/tierii/pages/TierIIOverviewPage.vue'),
    },
    {
      path: '/facility/:facilityID-:facilitySlug',
      children: [
        {
          path: 'hazwaste-overview',
          name: 'HazWaste Overview',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteOverviewPage.vue'),
        },
        {
          path: 'hazwaste-training-profile',
          name: 'HazWaste Training Profile',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/TrainingProfilePage.vue'),
        },
        {
          path: 'hazwaste-training-management',
          name: 'HazWaste Training Management',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/TrainingManagementPage.vue'),
        },
        {
          path: 'hazwaste-training-configuration',
          name: 'HazWaste Training Configuration',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/TrainingConfigurationPage.vue'),
        },
        {
          path: 'hazwaste-training-management/personnel/:userID',
          name: 'HazWaste Training Personnel Detail',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/TrainingPersonnelDetailPage.vue'),
        },
        {
          path: 'hazwaste-streams',
          name: 'HazWaste Waste Streams',
          props: (to) => ({ ...to.params, streamID: null, streamSlug: null }),
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteStreamsPage.vue'),
        },
        {
          path: 'hazwaste-streams/new',
          name: 'HazWaste Waste Stream New',
          props: (to) => ({ ...to.params, streamID: null, streamSlug: null }),
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteStreamsPage.vue'),
        },
        {
          path: 'hazwaste-streams/:streamID-:streamSlug',
          name: 'HazWaste Waste Stream Detail',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteStreamsPage.vue'),
        },
        {
          path: 'hazwaste-help',
          name: 'HazWaste Help',
          props: (to) => ({ ...to.params, articleID: null }),
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/WorkflowHelpPage.vue'),
        },
        {
          path: 'hazwaste-help/:articleID',
          name: 'HazWaste Help Article',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/WorkflowHelpPage.vue'),
        },
        {
          path: 'hazwaste-accumulation-areas/new',
          name: 'HazWaste Accumulation Area New',
          props: (to) => ({ ...to.params, locationID: null }),
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/StorageLocationDetailPage.vue'),
        },
        {
          path: 'hazwaste-accumulation-areas/:locationID-:locationSlug',
          name: 'HazWaste Accumulation Area Detail',
          props: (to) => {
            // Picked explicitly so the unused `locationSlug` param doesn't fall through as an HTML attribute.
            const { facilityID, facilitySlug, locationID } =
              to.params as RouteNamedMap['HazWaste Accumulation Area Detail']['params'];
            return { facilityID, facilitySlug, locationID };
          },
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/StorageLocationDetailPage.vue'),
        },
        {
          path: 'hazwaste-accumulation-areas',
          name: 'HazWaste Accumulation Areas',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/ContainerOperationsPage.vue'),
        },
        {
          path: 'hazwaste-site-map',
          name: 'HazWaste Site Map',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/StorageLocationsPage.vue'),
        },
        {
          path: 'hazwaste-quick-reference-guide',
          name: 'HazWaste Quick Reference Guide',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/QuickReferenceGuidePage.vue'),
        },
        {
          path: 'hazwaste-container-labels',
          name: 'HazWaste Container Labels',
          meta: { face: 'hazwaste' },
          redirect: (to) => ({ name: 'HazWaste Accumulation Areas', params: to.params as any }),
        },
        {
          path: 'hazwaste-inspections',
          name: 'HazWaste Inspections',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/InspectionsPage.vue'),
        },
        {
          path: 'hazwaste-tasks',
          name: 'HazWaste Tasks',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteTasksPage.vue'),
        },
        {
          path: 'hazwaste-manifests',
          name: 'HazWaste Manifests',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/ManifestsPage.vue'),
        },
        {
          path: 'hazwaste-other-shipments',
          name: 'HazWaste Other Shipments',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/OtherWasteShipmentsPage.vue'),
        },
      ],
    },

    // Default Redirects:
    {
      path: '/:year(\\d{4})/:destination',
      name: 'Facility Default Redirect',
      component: () => import('@/pages/FacilityDefaultRedirectPage.vue'),
    },
    {
      path: `/:year/facility/:facilityID-:facilitySlug`,
      children: [
        {
          path: '',
          name: 'Facility Status',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityStatusPage.vue'),
        },
        {
          path: 'overall',
          name: 'Overall Status',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/OverallStatusPage.vue'),
        },
        {
          path: 'anomalies',
          name: 'Anomalies',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AnomaliesPage.vue'),
        },
        {
          path: 'mass-actions',
          name: 'Mass Actions',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/MassActionsPage.vue'),
        },
        {
          path: 'usage',
          name: 'Facility Usage',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityUsagePage.vue'),
        },
        {
          path: 'activity-review',
          name: 'Facility Activity Review',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityActivityReviewPage.vue'),
        },
        {
          path: 'products',
          name: 'Facility Products',
          props: true,
          component: () => import('@/pages/FacilityProductsPage.vue'),
          children: [
            {
              path: ':productID-:productSlug',
              name: 'Product',
              props: true,
              component: () => import('@/pages/ProductPage.vue'),
            },
          ],
        },
        {
          path: 'thresholds',
          name: 'Facility Thresholds',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityThresholdChemicalsPage.vue'),
        },
        {
          path: 'inventory-profiles',
          children: [
            {
              path: 'list',
              name: 'Inventory Profiles',
              props: true,
              meta: { face: 'tierii' },
              component: () => import('@/pages/TenantReleaseProfilesPage.vue'),
            },
            {
              path: ':releaseProfileID-:releaseProfileSlug',
              name: 'Inventory Profile',
              props: true,
              meta: { face: 'tierii' },
              component: () => import('@/pages/TenantReleaseProfilePage.vue'),
            },
          ],
        },
        {
          path: 'release-profiles',
          children: [
            {
              path: 'assign',
              name: 'Facility Release Profiles',
              props: true,
              meta: { face: 'tri' },
              component: () => import('@/pages/FacilityReleaseProfilesPage.vue'),
            },
            {
              path: 'list',
              name: 'Release Profiles',
              props: true,
              component: () => import('@/pages/TenantReleaseProfilesPage.vue'),
            },
            {
              path: ':releaseProfileID-:releaseProfileSlug',
              name: 'Release Profile',
              props: true,
              component: () => import('@/pages/TenantReleaseProfilePage.vue'),
            },
          ],
        },
        {
          path: 'compliance-metrics',
          children: [
            {
              path: 'list',
              name: 'Compliance Metrics',
              props: true,
              meta: { face: 'emissions' },
              component: () => import('@/pages/TenantReleaseProfilesPage.vue'),
            },
            {
              path: ':releaseProfileID-:releaseProfileSlug',
              name: 'Compliance Metric',
              props: true,
              meta: { face: 'emissions' },
              component: () => import('@/pages/TenantReleaseProfilePage.vue'),
            },
          ],
        },
        {
          path: 'questionnaire',
          name: 'Facility Questionnaire',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityQuestionnaire.vue'),
        },
        {
          path: 'releases',
          name: 'Facility Release Review',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityReleaseReviewPage.vue'),
        },
        {
          path: 'form-r-details',
          name: 'Facility Form R Details',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityFormRDetails.vue'),
        },
        {
          path: 'form-r',
          name: 'Facility Form R',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityFormRPage.vue'),
        },
        {
          path: 'submit',
          name: 'Facility Form R Submit',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityFormRSubmitPage.vue'),
        },
        {
          path: 'exemption-review',
          name: 'Facility Exemption Review',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityExemptionReviewPage.vue'),
        },
        {
          path: 'receptors',
          name: 'Receptors',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/FacilityReceptorsPage.vue'),
        },
        {
          path: 'chemicals',
          name: 'Chemicals',
          component: () => import('@/pages/ChemicalsPage.vue'),
        },
        {
          path: 'chemicals/sync',
          name: 'Chemicals Sync',
          component: () => import('@/pages/ChemicalsSyncPage.vue'),
        },
        {
          path: 'facilities',
          name: 'Facilities',
          component: () => import('@/hazwaste/pages/HazWasteFacilitiesPage.vue'),
          props: () => {
            return {
              facility: facilityFromRoute.value,
            };
          },
          children: [],
        },
        {
          path: 'e-manifest',
          name: 'E-Manifest',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AdminEManifestPage.vue'),
        },
        {
          path: 'release-comparison',
          name: 'Admin Release Comparison',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AdminReleaseComparisonPage.vue'),
        },
        {
          path: 'release-profile-assignments-comparison',
          name: 'Admin Release Profile Assignments Comparison',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AdminReleaseProfileAssignmentsComparisonPage.vue'),
        },
        {
          path: 'release-snapshot-comparison',
          name: 'Admin Release Snapshot Comparison',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AdminReleaseSnapshotComparisonPage.vue'),
        },
        {
          path: 'usage-import',
          name: 'Usage Import',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/UsageImportPage.vue'),
        },
        {
          path: 'spreadsheet-agent',
          name: 'Spreadsheet Agent',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/SpreadsheetAgentPage.vue'),
        },
        {
          path: 'users',
          name: 'Users',
          component: () => import('../cross-app-frontend/users/UsersPage.vue'),
        },
        {
          path: 'help/tri-knowledge/:section?',
          name: 'TRI Knowledge',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/TRIKnowledgePage.vue'),
        },
        {
          path: 'help/app-help',
          name: 'App Help',
          props: true,
          meta: { face: 'tri' },
          component: () => import('@/pages/AppHelpPage.vue'),
        },

        // Emissions:
        {
          path: 'emissions-unit-types',
          name: 'Emissions Unit Types',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsUnitTypesPage.vue'),
        },
        {
          path: 'emissions-units',
          name: 'Emissions Units',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsUnitsPage.vue'),
        },
        {
          path: 'emissions-units/:emissionsUnitID-:emissionsUnitSlug/questionnaire',
          name: 'Emissions Questionnaire',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsQuestionnairePage.vue'),
        },
        {
          path: 'emissions-units/:emissionsUnitID-:emissionsUnitSlug/emissions',
          name: 'Emissions',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsQuestionnairePage.vue'),
        },
        {
          path: 'emissions-units/:emissionsUnitID-:emissionsUnitSlug/potential-emissions',
          name: 'Potential Emissions',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsQuestionnairePage.vue'),
        },
        {
          path: 'emissions-reports',
          name: 'Emissions Reports',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsReportsPage.vue'),
        },
        {
          // Reports used to be scoped to a unit. Users have saved links to this path:
          path: 'emissions-units/:emissionsUnitID-:emissionsUnitSlug/reports',
          name: 'Emissions Reports (Unit-Scoped)',
          meta: { face: 'emissions' },
          redirect: (to) => {
            const { year, facilityID, facilitySlug } = to.params as RouteNamedMap['Emissions Reports']['params'];
            return { name: 'Emissions Reports', params: { year, facilityID, facilitySlug } };
          },
        },
        {
          path: 'emissions-overview',
          name: 'Emissions Overview',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsOverviewPage.vue'),
        },
        {
          path: 'emissions-compliance',
          name: 'Emissions Limits',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsCompliancePage.vue'),
        },
        {
          path: 'emissions-compliance/:emissionsLimitID/history',
          name: 'Emissions Limit History',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsLimitHistoryPage.vue'),
        },
        {
          path: 'emissions-my-products',
          name: 'Emissions My Products',
          props: true,
          meta: { face: 'emissions' },
          component: () => import('@/pages/EmissionsMyProductsPage.vue'),
        },

        {
          path: 'inventory',
          name: 'TierII Inventory',
          meta: { face: 'tierii' },
          redirect: (to) => ({ name: 'TierII Inventory Questionnaire', params: to.params as any }),
        },
        {
          path: 'inventory/questionnaire/:productID?',
          name: 'TierII Inventory Questionnaire',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/FacilityInventoryQuestionnairePage.vue'),
        },
        {
          path: 'inventory/threshold',
          name: 'TierII Inventory Threshold',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/FacilityInventoryThresholdPage.vue'),
        },
        {
          path: 'report-form/:sectionKey?',
          name: 'TierII Report Form',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/FacilityReportFormPage.vue'),
        },
        {
          path: 'site-map',
          name: 'TierII Site Map',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/SiteMapPage.vue'),
        },
        {
          path: 'export',
          name: 'TierII Export',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/FacilityExportPage.vue'),
        },
        {
          path: 'status',
          name: 'TierII Status',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/TierIIOverallStatusPage.vue'),
        },
        {
          path: 'admin/jurisdictions',
          name: 'TierII Admin Jurisdictions',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/JurisdictionProfileAdminPage.vue'),
        },
        {
          path: 'admin/thresholds',
          name: 'TierII Admin Thresholds',
          props: true,
          meta: { face: 'tierii' },
          component: () => import('@/tierii/pages/JurisdictionThresholdsAdminPage.vue'),
        },
        {
          path: 'hazwaste-generation-log',
          name: 'HazWaste Generation Log',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/HazWasteGenerationLogPage.vue'),
        },
        {
          path: 'hazwaste-biennial-report',
          name: 'HazWaste Biennial Report',
          props: true,
          meta: { face: 'hazwaste' },
          component: () => import('@/hazwaste/pages/BiennialReportPage.vue'),
        },
      ],
    },

    // Utility:
    { path: '/no-access', name: 'No Access', component: () => import('../cross-app-frontend/NoAccessPage.vue') },
    { path: '/raffle', name: 'Raffle', component: () => import('@/pages/RafflePage.vue') },
    {
      path: '/platform/:tenantID?/:facilityID?',
      name: 'Platform',
      component: () => import('@/pages/PlatformLandingPage.vue'),
    },
    { path: '/:pathMatch(.*)*', name: 'Not Found', component: () => import('../cross-app-frontend/NotFoundPage.vue') },
  ],
});

const facilityLastVisited = ref<FacilityID | null>(null);
const yearLastVisited = ref<number>(new Date().getUTCFullYear() - 1);

router.beforeEach(async (to): Promise<boolean | RouteLocationRaw<keyof RouteNamedMap>> => {
  await userLoginStateConfirmedPromise;
  if (to.name === 'Raffle') return true;
  if (loginState.value !== 'logged-in' && to.name !== 'Login') {
    return { name: 'Login', query: { returnTo: to.fullPath }, replace: true };
  }

  if (loginState.value === 'logged-in' && to.name === 'Login') {
    if (facilitiesList.value.length === 0) {
      return { name: 'No Access', replace: true };
    }

    const { facilityID, name } = facilitiesList.value[0];
    return {
      name: 'Facility Status',
      params: { facilityID, facilitySlug: slugFromName(name), year: yearLastVisited.value },
    };
  }

  if (
    loginState.value === 'logged-in' &&
    to.name !== 'Platform' &&
    facilitiesList.value.length > 0 &&
    facilitiesList.value.every((f) => f.apps.length === 0)
  ) {
    const facility = facilitiesList.value[0];
    return {
      name: 'Platform',
      params: { tenantID: facility.tenantID, facilityID: facility.facilityID },
      replace: true,
    };
  }

  const targetFace = to.meta.face;
  if (targetFace && targetFace !== face.value) {
    const origin = window.location.origin;
    if (origin.includes(face.value)) {
      const newOrigin = origin.replace(face.value, targetFace);
      if (newOrigin !== origin) {
        window.location.href = newOrigin + to.fullPath;
        return false;
      }
    }
  }

  if (to.name === 'Facility Default Redirect') {
    if (facilitiesList.value.length === 0) {
      return { name: 'No Access', replace: true };
    }

    const year = Number(to.params.year) || new Date().getUTCFullYear();
    const destination = {
      status: 'Facility Status',
      usage: 'Facility Usage',
      'activity-review': 'Facility Activity Review',
      products: 'Facility Products',
      thresholds: 'Facility Thresholds',
      'release-profiles': 'Facility Release Profiles',
      'inventory-profiles': 'Inventory Profiles',
      questionnaire: 'Facility Questionnaire',
      releases: 'Facility Release Review',
      'form-r-details': 'Facility Form R Details',
      'form-r': 'Facility Form R',
      submit: 'Facility Form R Submit',
      'exemption-review': 'Facility Exemption Review',
      anomalies: 'Anomalies',
      'mass-actions': 'Mass Actions',
      'spreadsheet-agent': 'Spreadsheet Agent',
    }[to.params.destination as string];
    if (destination) {
      const { facilityID, name } = facilitiesList.value[0];
      return {
        name: destination,
        params: { facilityID, facilitySlug: slugFromName(name), year },
        hash: to.hash,
        replace: true,
      } as RouteLocationRaw<any>;
    }
  }

  if ('facilityID' in to.params && to.params.facilityID) {
    const facility = facilityFindFromRoute(to.params.facilityID);
    if (facility) {
      facilityLastVisited.value = facility.facilityID;
    } else if (!facilityLastVisited.value) {
      facilityLastVisited.value = facilitiesList.value[0]?.facilityID ?? null;
    }

    if (!facility) {
      return { path: '/404', replace: true };
    }
  } else if ('tenantID' in to.params && to.params.tenantID) {
    const paramTenantIDStr = String(to.params.tenantID);
    const facility = facilityFindFromRoute(facilityLastVisited.value);
    if (!facility || String(facility.tenantID) !== paramTenantIDStr) {
      const firstTenantFacility = facilitiesList.value.find((f) => String(f.tenantID) === paramTenantIDStr);
      facilityLastVisited.value = firstTenantFacility?.facilityID ?? null;
    }
  }

  if ('year' in to.params && to.params.year) {
    const yearNum = Number(to.params.year);
    if (yearNum > new Date().getFullYear() + 1) {
      return { path: '/404', replace: true };
    }

    yearLastVisited.value = yearNum;
  }

  const routeName = to.name?.toString() || '';
  if (routeName) {
    const pieces = [routeName];

    if ('year' in to.params) {
      pieces.push(to.params.year);
    }

    if ('facilityID' in to.params) {
      const facilityName = facilitiesByFacilityID.value[Number(to.params.facilityID) as FacilityID]?.name;
      if (facilityName) {
        pieces.push(facilityName);
      }
    }

    pieces.push(appTitle.value);

    document.title = pieces.join(' | ');
  } else {
    document.title = appTitle.value;
  }

  return true;
});

router.onError((error, to) => {
  if (!FIREBASE_USING_EMULATORS && error instanceof Error && /fetch|dynamic|module|script/i.test(error.message)) {
    console.error(`Router error, a deployment likely happened, doing a full page reload:`, error);
    window.location.href = to.fullPath;
  }
});

watch(loginState, (loginState, oldState) => {
  if (oldState === 'logged-in' && loginState !== 'logged-in') {
    let query = {};
    if (router.currentRoute.value.name !== 'Login') {
      query = { returnTo: router.currentRoute.value.fullPath };
    }
    router.push({ name: 'Login', query });
  }
});

export const breadcrumbOverrideMap = ref<Partial<Record<string, string>>>({
  'HazWaste Manifests': 'RCRA Shipments',
  'HazWaste Other Shipments': 'Other Shipments',
});
export const breadcrumbs = computed((): { id: number; title: string; to: RouterLinkProps['to'] }[] => {
  return router.currentRoute.value.matched
    .filter((r) => !!r.name)
    .map((route, index) => {
      const name = route.name as string;

      const params: Record<string, string | string[]> = {};
      for (const [key, value] of Object.entries(router.currentRoute.value.params)) {
        if (route.path.includes(':' + key)) {
          params[key] = value;
        }
      }

      return {
        id: index,
        title: breadcrumbOverrideMap.value[name] ?? name,
        to: { name: route.name as any, params },
      };
    });
});

const facilityFromRoute = computed((): FacilityBrief | null => {
  if ('facilityID' in router.currentRoute.value.params) {
    const facility = facilityFindFromRoute(router.currentRoute.value.params.facilityID);
    if (facility) {
      return facility;
    }
  }

  return null;
});

export const facilityFromRouteOrLastVisited = computed((): FacilityBrief | null => {
  const fromRoute = facilityFromRoute.value;
  if (fromRoute) {
    return fromRoute;
  }

  const lastVisited = facilitiesList.value.find((f) => f.facilityID === facilityLastVisited.value);
  if (lastVisited) {
    return lastVisited;
  }

  return facilitiesList.value[0] ?? null;
});

export async function facilityUpdateInRoute(facility: FacilityBrief): Promise<void> {
  const route = router.currentRoute.value;
  if ('facilityID' in route.params && route.params.facilityID) {
    await router.push({
      name: route.name,
      params: { ...route.params, facilityID: facility.facilityID, facilitySlug: slugFromName(facility.name) },
      query: route.query,
      hash: route.hash,
    } as RouteLocationRaw<any>);
  } else if ('tenantID' in route.params && route.params.tenantID) {
    facilityLastVisited.value = facility.facilityID;
    await router.push({
      name: route.name,
      params: { ...route.params, tenantID: facility.tenantID },
      query: route.query,
      hash: route.hash,
    } as RouteLocationRaw<any>);
  } else {
    facilityLastVisited.value = facility.facilityID;
  }
}

const yearFromRoute = computed((): number | null => {
  if ('year' in router.currentRoute.value.params) {
    const year = router.currentRoute.value.params.year;
    if (year) {
      return Number(year);
    }
  }

  return null;
});

export const yearFromRouteOrLastVisited = computed((): number => {
  const fromRoute = yearFromRoute.value;
  if (fromRoute) {
    return fromRoute;
  }

  return yearLastVisited.value;
});

export async function yearUpdateInRoute(year: number): Promise<void> {
  const route = router.currentRoute.value;
  if ('year' in route.params && route.params.year) {
    await router.push({
      name: route.name,
      params: { ...route.params, year },
      query: route.query,
      hash: route.hash,
    } as RouteLocationRaw<any>);
  } else {
    yearLastVisited.value = year;
  }
}
